import { ReadState, unavailableRead } from "@/components/LoadingStates";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";

import { FollowUpChip } from "@/components/FollowUpControls";
import { useMyFollowUps } from "@/hooks/useMyFollowUps";
import { classNames } from "@/lib/format";
import MediaImage from "@/components/MediaImage";
import { explicitDemoMedia } from "@/lib/media";
import { ClipboardCheck, ArrowRight, MessagesSquare } from "lucide-react";
import { isFollowUpItem, splitItems } from "@/lib/decisionItems";

// OP-036: what the line says about the video, without inventing anything when its catalogue document is gone.
export function videoContext(d) {
  if (d.video_available === false) return `Video unavailable · ${d.video_title || "titre inconnu"} (titre enregistré sur la note)`;
  const label = [d.creator_name, d.video_title].filter(Boolean).join(" · ");
  return d.saved === false ? `Removed from Saved · ${label}` : label;
}

// headingLevel: 3 by default (unchanged for existing callers); a page whose sections are h2 passes 2.
export default function MyFollowUps({ limit = 5, headingLevel = 3 }) {
  const Heading = headingLevel === 2 ? "h2" : "h3";
  const query = useMyFollowUps(limit);
  const { data, isFetching } = query;
  // OP-036: the payload shape is checked as a whole; each row on its own (an invalid row is counted, not fatal).
  const malformed = !query.isPending && (!data || !Array.isArray(data.items) || typeof data.total !== "number");
  const { items, unreadable } = splitItems(data?.items, isFollowUpItem);
  const setAside = unreadable + (typeof data?.malformed_total === "number" ? data.malformed_total : 0);
  const read = { status: query.isError ? (unavailableRead(query.error) ? "unavailable" : "error") : query.isPending ? "loading" : malformed ? "error" : "success", error: malformed ? new Error("Données reçues inutilisables.") : query.error, reload: () => query.refetch() };

  return (
    <Card className="p-5 space-y-3" data-testid="my-follow-ups" data-refreshing={isFetching ? "true" : "false"}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-4 w-4 text-[hsl(var(--amber))]" />
          <Heading className="op-section-title">My Follow-ups</Heading>
          {data && data.open > 0 && (
            <span className={classNames("rounded-full px-2 py-0.5 text-[10px] font-bold font-mono", data.overdue > 0 ? "bg-[hsl(var(--rose))] text-white" : "bg-[hsl(var(--amber))]/15 text-[hsl(var(--amber))]")} data-testid="my-follow-ups-count">
              {data.open}{data.overdue > 0 ? ` · ${data.overdue} overdue` : ""}
            </span>
          )}
        </div>
        <Link to="/decisions?assignee=me" className="text-xs font-medium text-primary hover:underline inline-flex items-center gap-1" data-testid="my-follow-ups-view-all">View all <ArrowRight className="h-3 w-3" /></Link>
      </div>

      <ReadState read={read} scope="my-follow-ups" empty={!items.length && !setAside} title="Aucun suivi ne vous est assigné">
        <ol className="divide-y divide-border" data-testid="my-follow-ups-list">
          {items.map((d) => (
            <li key={d.id} className="py-2.5 first:pt-0 last:pb-0" data-testid={`my-follow-up-${d.id}`}>
              <Link to={d.thread_path} className="flex gap-3 group">
                <MediaImage src={d.thumbnail} alt="" demo={explicitDemoMedia(d)} showLabel={false} className="h-12 w-9 shrink-0 rounded object-cover bg-muted" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium leading-snug line-clamp-2 group-hover:text-primary transition-colors" data-testid={`my-follow-up-body-${d.id}`}>{d.body}</p>
                  <div className="mt-1 flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
                    <FollowUpChip fu={d.follow_up} state={d.follow_up_state} testid={`my-follow-up-chip-${d.id}`} />
                    {d.follow_up?.due_date && <span className="font-mono">due {d.follow_up.due_date}</span>}
                    <span className="truncate" data-testid={`my-follow-up-video-${d.id}`}>{videoContext(d)}</span>
                  </div>
                </div>
                <MessagesSquare className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mt-1" />
              </Link>
            </li>
          ))}
          {data && Array.isArray(data.items) && data.total > data.items.length && (
            <li className="pt-2 text-xs text-muted-foreground" data-testid="my-follow-ups-more">+{data.total - data.items.length} more on the board</li>
          )}
          {data?.truncated && <li className="pt-2 text-xs text-muted-foreground" data-testid="my-follow-ups-truncated">Calculé sur les {data.limit} décisions les plus récentes : des suivis plus anciens peuvent manquer.</li>}
          {setAside > 0 && <li className="pt-2 text-xs text-muted-foreground" data-testid="my-follow-ups-malformed">{setAside} décision(s) illisible(s) n’ont pas pu être prises en compte.</li>}
        </ol>
      </ReadState>
    </Card>
  );
}
